import { stringify } from 'query-string';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook, type Action } from '@atlassian/react-sweet-state';
import type { Approver } from '../../common/types.tsx';
import { fetchedUserToApprover } from '../../utils/approver-parser.tsx';
import type { BulkUserResponse, FetchedUser } from './types.tsx';

type State = {
	isLoading: boolean;
	cachedApprovers: Approver[];
	error: Error | undefined;
};

const initialState: State = {
	isLoading: false,
	cachedApprovers: [],
	error: undefined,
};

const fetchUsers = (query: string): Promise<BulkUserResponse> =>
	fetchJson(`/rest/api/3/user/bulk?${query}`);

export const transformResponse = (fetchedUsers: FetchedUser[]): Approver[] =>
	fetchedUsers.filter(Boolean).map(fetchedUserToApprover);

const fetchApprovers =
	(userIds: AccountId[]): Action<State> =>
	async ({ getState, setState }) => {
		setState({
			isLoading: true,
		});
		if (userIds.length === 0) {
			setState({
				isLoading: false,
			});
			return;
		}
		try {
			const { values: users } = await fetchUsers(stringify({ accountId: userIds }));
			const approvers = transformResponse(users);
			const { cachedApprovers } = getState();
			setState({
				isLoading: false,
				cachedApprovers: Array.from(new Set([...cachedApprovers, ...approvers])),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			trackFetchErrors({
				error,
				id: 'fetchApprovers',
				packageName: 'jiraWorkflowApprovals',
				sendToPrivacyUnsafeSplunk: true,
			});
			setState({ isLoading: false, error });
		}
	};

const updateCachedApprovers =
	(approversToCache: Approver[]): Action<State> =>
	({ getState, setState }) => {
		const { cachedApprovers } = getState();
		setState({
			cachedApprovers: Array.from(new Set([...approversToCache, ...cachedApprovers])),
		});
	};

export const getCachedApproversForIds = (accountIds: AccountId[], cachedApprovers: Approver[]) =>
	accountIds
		.map((id) => cachedApprovers.find((cachedApprover) => cachedApprover.accountId === id))
		.filter(Boolean);

export const actions = {
	fetchApprovers,
	updateCachedApprovers,
} as const;

const Store = createStore({ initialState, actions, name: 'approvals-approver-service' });

const useApproverService = createHook(Store);

export default useApproverService;
